import type React from "react";
import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  Link,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { IStreamDetails } from "../../../types/streams";
import EventFields from "./components/EventCardFields";

interface EventDetailsCardProps {
  stream: IStreamDetails;
}

const EventDetailsCard: React.FC<EventDetailsCardProps> = ({ stream }) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [showFullNotes, setShowFullNotes] = useState(false);

  const MAX_NOTES_LENGTH = 70;
  const notes = stream?.service?.service_notes ?? "";
  const isNotesLong = notes.length > MAX_NOTES_LENGTH;
  const notesDisplayText =
    isNotesLong && !showFullNotes
      ? notes.substring(0, MAX_NOTES_LENGTH) + "..."
      : notes;
  const special_request = stream?.service?.special_request ?? "";
  const isSpecialRequestLong = notes.length > MAX_NOTES_LENGTH;
  const specialrequestDisplayText =
    isSpecialRequestLong && !showFullNotes
      ? special_request.substring(0, MAX_NOTES_LENGTH) + "..."
      : special_request;

  const LinkField: React.FC<{ label: string; url: string }> = ({
    label,
    url,
  }) => (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="body2"
        sx={{ color: reduxTheme.primary_colors[300] }}
        gutterBottom
      >
        {label}
      </Typography>
      <Paper
        sx={{
          p: 1.5,
          boxShadow: 0,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.grey[50],
        }}
      >
        {url ? (
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "none", color: "primary.main" }}
          >
            <Typography>{url}</Typography>
          </Link>
        ) : (
          <Typography>N/A</Typography>
        )}
      </Paper>
    </Box>
  );
  console.log(stream.booking?.service);

  return (
    <Card sx={{ height: "100%", boxShadow: 0 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Event Details
        </Typography>
        <Grid container spacing={2}>
          <EventFields stream={stream} />
        </Grid>

        {/* Notes Section */}
        <Box mt={2}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: 14,
              fontWeight: "medium",
              mb: 1,
              color: reduxTheme.primary_colors[300],
            }}
          >
            Notes
          </Typography>
          <Paper
            sx={{
              boxShadow: 0,
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: 14, color: reduxTheme.primary_colors[100] }}
            >
              {notesDisplayText || "No notes available"}
              {isNotesLong && (
                <Typography
                  component="span"
                  sx={{
                    color: reduxTheme.primary_colors[500],
                    fontWeight: "medium",
                    cursor: "pointer",
                    ml: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => setShowFullNotes(!showFullNotes)}
                >
                  {showFullNotes ? " show less" : " view more"}
                </Typography>
              )}
            </Typography>
          </Paper>
        </Box>

        <Box mt={2}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: 14,
              fontWeight: "medium",
              mb: 1,
              color: reduxTheme.primary_colors[300],
            }}
          >
            Special Request
          </Typography>
          <Paper
            sx={{
              boxShadow: 0,
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: 14, color: reduxTheme.primary_colors[100] }}
            >
              {specialrequestDisplayText || "No notes available"}
              {isSpecialRequestLong && (
                <Typography
                  component="span"
                  sx={{
                    color: reduxTheme.primary_colors[500],
                    fontWeight: "medium",
                    cursor: "pointer",
                    ml: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => setShowFullNotes(!showFullNotes)}
                >
                  {showFullNotes ? " show less" : " view more"}
                </Typography>
              )}
            </Typography>
          </Paper>
        </Box>

        <LinkField
          label="Youtube Streamlink"
          url={stream.youtube_stream_link || ""}
        />
        <LinkField label="Go Fund Me Link" url={stream.go_fund_me_link || ""} />
        <LinkField
          label="Website Streamlink"
          url={stream.website_stream_link || ""}
        />
      </CardContent>
    </Card>
  );
};

export default EventDetailsCard;
